import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

function MediaTile({ item, index, onOpen, onBroken }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.06 }}
      onClick={() => onOpen(index)}
      className={`group relative overflow-hidden rounded-3xl border border-white/10 text-left transition-all hover:border-[#f8b818]/50 ${index % 5 === 0 ? "sm:col-span-2 lg:row-span-2" : ""}`}
      data-testid={`gallery-item-${index}`}
    >
      {item.media_type === "video" ? (
        <video src={item.src} muted playsInline preload="metadata" onError={() => onBroken(item.id)} className="h-full w-full object-cover" />
      ) : (
        <img src={item.src} alt={item.title || "Cool Runnings park"} loading="lazy" onError={() => onBroken(item.id)} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-80" />
      {item.media_type === "video" && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="rounded-full bg-[#f8b818] p-4 text-[#0a0a0b] shadow-lg transition-transform group-hover:scale-110">
            <Play size={22} className="fill-[#0a0a0b]" />
          </span>
        </span>
      )}
      {item.title && (
        <span className="absolute bottom-4 left-4 right-4 text-sm font-extrabold text-[#faf5ea]">{item.title}</span>
      )}
    </motion.button>
  );
}

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [broken, setBroken] = useState({});
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(-1);
  // every photo lives on one page — no pagination
  // any media whose file fails to load (dead storage entries) is dropped everywhere
  const markBroken = useCallback((id) => setBroken((b) => (b[id] ? b : { ...b, [id]: true })), []);
  const goodItems = items.filter((it) => !broken[it.id]);
  const visibleItems = goodItems;

  useEffect(() => {
    api.get("/media")
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const close = useCallback(() => setActive(-1), []);
  useEffect(() => {
    if (active === -1) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") setActive((a) => Math.min(a + 1, goodItems.length - 1));
      if (e.key === "ArrowLeft") setActive((a) => Math.max(a - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, goodItems.length, close]);

  const item = active >= 0 ? goodItems[active] : null;

  return (
    <section id="gallery" className="border-y border-[#241c0d]/10 bg-[#fff1d9] py-24" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="mt-3 max-w-2xl text-4xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)] sm:text-5xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>LIFE ON THE TRACK</h2>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-[#f8b818]" size={28} />
          </div>
        ) : goodItems.length === 0 ? (
          <div className="card-dark mt-10 p-12 text-center" data-testid="gallery-empty-state">
            <p className="font-display text-2xl text-[#f8b818]">FIRST PHOTOS COMING SOON</p>
            <p className="mt-2 text-sm text-[#8a7b5e]">The park team is uploading media — check back shortly.</p>
          </div>
        ) : (
          <>
            <div className="mt-12 grid auto-rows-[180px] grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:auto-rows-[220px]" data-testid="gallery-grid">
              {visibleItems.map((it, i) => (
                <MediaTile
                  key={it.id}
                  item={it}
                  index={i}
                  onBroken={markBroken}
                  onOpen={(idx) => setActive(idx)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {item && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/95 p-4"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute right-5 top-5 rounded-full border border-white/40 bg-white/10 p-3 text-white shadow-lg backdrop-blur-sm transition-colors hover:border-[#f8b818] hover:bg-[#f8b818] hover:text-[#0a0a0b]"
              onClick={close}
              aria-label="Close"
              data-testid="gallery-lightbox-close-btn"
            >
              <X size={20} />
            </button>
            <button
              className="absolute left-3 rounded-full border border-white/20 p-3 text-white hover:border-[#f8b818] hover:text-[#f8b818] disabled:opacity-30"
              onClick={(e) => { e.stopPropagation(); setActive((a) => Math.max(a - 1, 0)); }}
              disabled={active === 0}
              aria-label="Previous"
              data-testid="gallery-lightbox-prev-btn"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className="absolute right-3 rounded-full border border-white/20 p-3 text-white hover:border-[#f8b818] hover:text-[#f8b818] disabled:opacity-30"
              onClick={(e) => { e.stopPropagation(); setActive((a) => Math.min(a + 1, goodItems.length - 1)); }}
              disabled={active === goodItems.length - 1}
              aria-label="Next"
              data-testid="gallery-lightbox-next-btn"
            >
              <ChevronRight size={20} />
            </button>
            <motion.figure
              key={item.id}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="flex max-h-[85vh] max-w-5xl flex-col items-center gap-3"
              onClick={(e) => e.stopPropagation()}
            >
              {item.media_type === "video" ? (
                <video src={item.src} controls autoPlay className="max-h-[75vh] rounded-2xl" data-testid="gallery-lightbox-video" />
              ) : (
                <img src={item.src} alt={item.title || "Cool Runnings park"} className="max-h-[75vh] rounded-2xl object-contain" data-testid="gallery-lightbox-image" />
              )}
              {item.title && <figcaption className="text-sm font-bold text-[#faf5ea]/85">{item.title}</figcaption>}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
